import { createContext, useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from '@tanstack/react-query'
import * as tokenService from "@service/tokenService"
import { useRefreshTokenOnStartQuery } from "@query/authQuery";
import { clearClientPersistence } from "@service/sessionCleanupService";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isInit, setIsInit] = useState(false);
  const queryClient = useQueryClient();
  const previousIsLoggedInRef = useRef(false);

  const isLoggedIn = useMemo(() => Boolean(accessToken && refreshToken), [accessToken, refreshToken]);

  useEffect(() => {
    if (!isInit) return;

    // Sync tokens to cookies
    if (accessToken) {
      tokenService.setAccessToken(accessToken);
    } else {
      tokenService.removeAccessToken();
    }

    if (refreshToken) {
      tokenService.setRefreshToken(refreshToken);
    } else {
      tokenService.removeRefreshToken();
    }

  }, [isInit, accessToken, refreshToken]);

  useEffect(() => {
    if (!isInit) return;

    const wasLoggedIn = previousIsLoggedInRef.current;
    previousIsLoggedInRef.current = isLoggedIn;

    if (!wasLoggedIn || isLoggedIn) {
      return;
    }

    void (async () => {
      try {
        await queryClient.cancelQueries();
        queryClient.clear();
        await clearClientPersistence();
      } catch (error) {
        console.warn("Failed to fully clear client state on logout.", error);
      }
    })();
  }, [isInit, isLoggedIn, queryClient]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, isInit, accessToken, setAccessToken, refreshToken, setRefreshToken }}>
      {!isInit && <RefreshTokenInitializer setIsInit={setIsInit} setAccessToken={setAccessToken} setRefreshToken={setRefreshToken}/>}
      {children}
    </AuthContext.Provider>
  );
};

const RefreshTokenInitializer = ({setIsInit, setAccessToken, setRefreshToken}) => {
  const refreshTokenOnStartQuery = useRefreshTokenOnStartQuery();

  useEffect(() => {
    if (refreshTokenOnStartQuery.isFetching) return;

    if (refreshTokenOnStartQuery.isSuccess && refreshTokenOnStartQuery.data?.data) {
      const { access_token, refresh_token } = refreshTokenOnStartQuery.data.data;
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
    } else if (refreshTokenOnStartQuery.isError) {
      setAccessToken(null);
      setRefreshToken(null);
    }

    setIsInit(true);
  }, [refreshTokenOnStartQuery, setIsInit, setAccessToken, setRefreshToken]);

  return null;
};

export default RefreshTokenInitializer;

